import { differenceInMilliseconds } from 'date-fns';
import {
  AddressContactDto,
  CreateQuantiferonTestDto,
  CreateQuantiferonTestDtoInterpretation,
  CreateSputumTestDto,
  CreateTspotTestDto,
  CreateTuberculinTestDto,
  CreateXrayTestDto,
  CreateXrayTestDtoFinding,
  CreateXrayTestDtoFollowup,
  LabOrderDto,
  LabOrderDtoInterpretation,
  LabOrderDtoType,
  LabOrderResultDto,
  LabOrderResultDtoType,
  SputumTestMaterialDto,
  SputumTestMaterialDtoTestMaterial,
  SputumTestMaterialResultDto,
  SputumTestMaterialResultDtoDetectionMethod,
  SputumTestMaterialResultDtoTestResult,
} from '../api/generated/model';
import { formatLocalDateTimeWithGermanMonth } from './date.ts';
import { stringToNumberOrUndefined } from './number.ts';

/** Maps a selected contact to the AddressContactDto shape expected by lab-order APIs (without nested address or isPinned). */
export function toLabOrderSamplingContact(
  contact: AddressContactDto | null | undefined,
): AddressContactDto | undefined {
  if (!contact) return undefined;

  const { address: _address, isPinned: _isPinned, ...contactFields } = contact;

  return contactFields;
}

export function getLabOrderTypeName(type: LabOrderDtoType) {
  switch (type) {
    case LabOrderDtoType.BLOOD_QUANTIFERON:
      return 'QFT';
    case LabOrderDtoType.BLOOD_TSPOT:
      return 'T-Spot';
    case LabOrderDtoType.SPUTUM:
      return 'Sputum';
    case LabOrderDtoType.TUBERCULIN:
      return 'Tuberkulin';
    case LabOrderDtoType.XRAY:
      return 'Röntgen';
  }
}

export function getLabOrderResultInterpretationName(interpretation: CreateQuantiferonTestDtoInterpretation) {
  switch (interpretation) {
    case CreateQuantiferonTestDtoInterpretation.POSITIVE:
      return 'Positiv';
    case CreateQuantiferonTestDtoInterpretation.NEGATIVE:
      return 'Negativ';
    case CreateQuantiferonTestDtoInterpretation.INDETERMINATE:
      return 'Unbestimmt';
    case CreateQuantiferonTestDtoInterpretation.NOT_EXECUTED:
      return 'Nicht gemacht';
    case CreateQuantiferonTestDtoInterpretation.PENDING:
      return 'Ausstehend';
  }
}

export function getXrayInterpretationName(interpretation: CreateXrayTestDtoFinding) {
  switch (interpretation) {
    case CreateXrayTestDtoFinding.NORMAL:
      return 'Normal';
    case CreateXrayTestDtoFinding.ABNORMAL_TB_SUSPICION:
      return 'Abnormal mit TB-Verdacht';
    case CreateXrayTestDtoFinding.ABNORMAL_TB_UNLIKELY:
      return 'Abnormal kein TB-Verdacht';
  }
}
export function getXrayFollowupName(followup: CreateXrayTestDtoFollowup) {
  switch (followup) {
    case CreateXrayTestDtoFollowup.MRI:
      return 'MRI';
    case CreateXrayTestDtoFollowup.SPUTUM_TEST:
      return 'Sputum-Test';
    case CreateXrayTestDtoFollowup.CT_SCAN:
      return 'CT Scan';
    case CreateXrayTestDtoFollowup.NONE:
      return 'Keines';
  }
}

export function getLabOrderResultDtoTypeName(type: LabOrderResultDtoType) {
  switch (type) {
    case LabOrderResultDtoType.QUANTIFERON_FINDING:
    case LabOrderResultDtoType.XRAY_FINDING:
    case LabOrderResultDtoType.TUBERCULIN_INDURATION:
      return 'Befund';
    case LabOrderResultDtoType.TUBERCULIN_READ_TIME:
      return 'Abgelesen';
    case LabOrderResultDtoType.QUANTIFERON_MITOGEN:
      return 'Mitogen';
    case LabOrderResultDtoType.QUANTIFERON_NIL_NOT_STIMULATED:
      return 'NIL (nicht stimuliert)';
    case LabOrderResultDtoType.QUANTIFERON_TB_ANTIGEN_1:
      return 'TB-Antigen 1';
    case LabOrderResultDtoType.QUANTIFERON_TB_ANTIGEN_2:
      return 'TB-Antigen 2';
    case LabOrderResultDtoType.TSPOT_ESAT6:
      return 'ESAT-6';
    case LabOrderResultDtoType.TSPOT_CFP10:
      return 'CFP-10';
    case LabOrderResultDtoType.SPUTUM_MATERIAL_AIRWAYS:
      return 'Material aus den Luftwegen';
    case LabOrderResultDtoType.SPUTUM_MATERIAL_SPUTUM:
      return 'Sputum';
    case LabOrderResultDtoType.SPUTUM_MATERIAL_CUSTOM:
      return 'Anderes Material';
    case LabOrderResultDtoType.SPUTUM_METHOD_SPUTUM_MICROSCOPICAL:
    case LabOrderResultDtoType.SPUTUM_METHOD_AIRWAYS_MICROSCOPICAL:
    case LabOrderResultDtoType.SPUTUM_METHOD_CUSTOM_MICROSCOPICAL:
      return 'Mikroskopisch';
    case LabOrderResultDtoType.SPUTUM_METHOD_SPUTUM_MOLECULAR:
    case LabOrderResultDtoType.SPUTUM_METHOD_AIRWAYS_MOLECULAR:
    case LabOrderResultDtoType.SPUTUM_METHOD_CUSTOM_MOLECULAR:
      return 'Molekuar-biologisch';
    case LabOrderResultDtoType.SPUTUM_METHOD_SPUTUM_CULTURE:
    case LabOrderResultDtoType.SPUTUM_METHOD_AIRWAYS_CULTURE:
    case LabOrderResultDtoType.SPUTUM_METHOD_CUSTOM_CULTURE:
      return 'Kultur';
    case LabOrderResultDtoType.XRAY_RX_NUMBER:
      return 'RX-Nummer';
    case LabOrderResultDtoType.XRAY_FILE_LINK:
      return 'Link zu Originaldatei';
    case LabOrderResultDtoType.XRAY_FOLLOWUP:
      return 'Folgeprozedere';
    case LabOrderResultDtoType.XRAY_FINDING_DOCTOR:
      return 'Befund Arzt';
    case LabOrderResultDtoType.XRAY_FINDING_COMMENT:
      return 'Kommentar Röntgenbefund';
  }
}

function getLabOrderSamplingTimestamp(order: LabOrderDto) {
  return new Date(order.samplingTime ? `${order.samplingDate}T${order.samplingTime}` : order.samplingDate).getTime();
}

export function compareLabOrdersByRecency(a: LabOrderDto, b: LabOrderDto): number {
  const samplingDiff = getLabOrderSamplingTimestamp(b) - getLabOrderSamplingTimestamp(a);
  if (samplingDiff !== 0) return samplingDiff;

  return differenceInMilliseconds(b.created ?? 0, a.created ?? 0);
}

export function getLatestLabOrder(labOrders: LabOrderDto[]) {
  if (!labOrders.length) return undefined;

  return [...labOrders].sort(compareLabOrdersByLatest)[0];
}

export function compareLabOrdersByLatest(a: LabOrderDto, b: LabOrderDto) {
  const samplingDateComparison = differenceInMilliseconds(b.samplingDate, a.samplingDate);

  if (samplingDateComparison !== 0) return samplingDateComparison;

  return differenceInMilliseconds(b.created ?? 0, a.created ?? 0);
}

export function getLabOrderResultName(type: LabOrderResultDtoType, value: string) {
  switch (type) {
    case LabOrderResultDtoType.SPUTUM_METHOD_SPUTUM_MICROSCOPICAL:
    case LabOrderResultDtoType.SPUTUM_METHOD_AIRWAYS_MICROSCOPICAL:
    case LabOrderResultDtoType.SPUTUM_METHOD_CUSTOM_MICROSCOPICAL:
    case LabOrderResultDtoType.SPUTUM_METHOD_SPUTUM_MOLECULAR:
    case LabOrderResultDtoType.SPUTUM_METHOD_AIRWAYS_MOLECULAR:
    case LabOrderResultDtoType.SPUTUM_METHOD_CUSTOM_MOLECULAR:
    case LabOrderResultDtoType.SPUTUM_METHOD_SPUTUM_CULTURE:
    case LabOrderResultDtoType.SPUTUM_METHOD_AIRWAYS_CULTURE:
    case LabOrderResultDtoType.SPUTUM_METHOD_CUSTOM_CULTURE:
      return getLabOrderDtoInterpretationNameFromString(value);
    case LabOrderResultDtoType.XRAY_FOLLOWUP:
      return getLabOrderXrayFollowupNameFromString(value);
    case LabOrderResultDtoType.XRAY_FINDING:
      return getLabOrderXrayInterpretationNameFromString(value);
    case LabOrderResultDtoType.TUBERCULIN_READ_TIME:
      return formatLocalDateTimeWithGermanMonth(value) ?? value;
    default:
      return value;
  }
}

export function transformLabOrderToCreateTestDto(order: LabOrderDto) {
  switch (order.type) {
    case LabOrderDtoType.TUBERCULIN:
      return transformLabOrderToTuberculinTestDto(order);
    case LabOrderDtoType.XRAY:
      return transformLabOrderToXrayTestDto(order);
    case LabOrderDtoType.SPUTUM:
      return transformLabOrderToSputumTestDto(order);
    case LabOrderDtoType.BLOOD_TSPOT:
      return transformLabOrderToTspotTestDto(order);
    case LabOrderDtoType.BLOOD_QUANTIFERON:
      return transformLabOrderToQuantiferonTestDto(order);
    default:
      return undefined;
  }
}

export function hasLabOrderResults(order: LabOrderDto) {
  if (order.interpretation === LabOrderDtoInterpretation.PENDING) {
    return false;
  }
  const xrayFindingResult = order.results.filter((result) => result.type === LabOrderResultDtoType.XRAY_FINDING);

  return !(order.type === LabOrderDtoType.XRAY && xrayFindingResult.length <= 0);
}

function transformLabOrderToTuberculinTestDto(order: LabOrderDto): CreateTuberculinTestDto {
  const indurationSizeResult = order.results.find(
    (result) => result.type === LabOrderResultDtoType.TUBERCULIN_INDURATION,
  );
  const readTimeResult = order.results.find((result) => result.type === LabOrderResultDtoType.TUBERCULIN_READ_TIME);

  return {
    appointmentId: order.appointmentId,
    indurationSize: indurationSizeResult?.result ? stringToNumberOrUndefined(indurationSizeResult?.result) : undefined,
    readTime: readTimeResult?.result ?? undefined,
    interpretation: order.interpretation,
    notes: order.notes,
    patientId: order.patient.id,
    samplingDate: order.samplingDate,
    samplingTime: order.samplingTime,
    samplingAddress: order.samplingAddress,
    samplingContact: order.samplingContact,
    isTodo: false,
  };
}

function transformLabOrderToXrayTestDto(order: LabOrderDto): CreateXrayTestDto {
  const fileLinkResult = order.results.find((result) => result.type === LabOrderResultDtoType.XRAY_FILE_LINK);
  const findingResult = order.results.find((result) => result.type === LabOrderResultDtoType.XRAY_FINDING);
  const findingCommentResult = order.results.find(
    (result) => result.type === LabOrderResultDtoType.XRAY_FINDING_COMMENT,
  );
  const findingDoctorResult = order.results.find((result) => result.type === LabOrderResultDtoType.XRAY_FINDING_DOCTOR);
  const followupResult = order.results.find((result) => result.type === LabOrderResultDtoType.XRAY_FOLLOWUP);
  const rxNumberResult = order.results.find((result) => result.type === LabOrderResultDtoType.XRAY_RX_NUMBER);

  return {
    appointmentId: order.appointmentId,
    file: order.fileDetails?.fileName
      ? {
        fileName: order.fileDetails.fileName,
        file: '',
      }
      : undefined,
    fileLink: fileLinkResult?.result,
    finding: findingResult?.result ? getLabOrderXrayInterpretationStatusFromString(findingResult.result) : undefined,
    findingComment: findingCommentResult?.result,
    findingDoctor: findingDoctorResult?.result,
    followup: followupResult?.result ? getLabOrderXrayFollowupStatusFromString(followupResult.result) : undefined,
    notes: order.notes,
    patientId: order.patient.id,
    rxNumber: rxNumberResult?.result,
    samplingDate: order.samplingDate,
    samplingTime: order.samplingTime,
    samplingAddress: order.samplingAddress,
    samplingContact: order.samplingContact,
    isTodo: false,
  };
}

function transformLabOrderToSputumTestDto(order: LabOrderDto): CreateSputumTestDto {
  const materialSputum = getSputumTestMaterialDto(
    order.results,
    LabOrderResultDtoType.SPUTUM_MATERIAL_SPUTUM,
    'SPUTUM_METHOD_SPUTUM',
  );
  const materialAirways = getSputumTestMaterialDto(
    order.results,
    LabOrderResultDtoType.SPUTUM_MATERIAL_AIRWAYS,
    'SPUTUM_METHOD_AIRWAYS',
  );
  const materialCustom = getSputumTestMaterialDto(
    order.results,
    LabOrderResultDtoType.SPUTUM_MATERIAL_CUSTOM,
    'SPUTUM_METHOD_CUSTOM',
  );

  return {
    appointmentId: order.appointmentId,
    file: order.fileDetails?.fileName
      ? {
        fileName: order.fileDetails.fileName,
        file: '',
      }
      : undefined,
    interpretation: order.interpretation,
    materials: [materialSputum, materialAirways, materialCustom].filter((material) => !!material),
    notes: order.notes,
    patientId: order.patient.id,
    samplingDate: order.samplingDate,
    samplingTime: order.samplingTime,
    samplingAddress: order.samplingAddress,
    samplingContact: order.samplingContact,
    isTodo: false,
  };
}

function transformLabOrderToTspotTestDto(order: LabOrderDto): CreateTspotTestDto {
  const esat6Result = order.results.find((result) => result.type === LabOrderResultDtoType.TSPOT_ESAT6);
  const cfp10Result = order.results.find((result) => result.type === LabOrderResultDtoType.TSPOT_CFP10);

  return {
    patientId: order.patient.id,
    samplingDate: order.samplingDate,
    samplingTime: order.samplingTime,
    samplingAddress: order.samplingAddress,
    samplingContact: order.samplingContact,
    appointmentId: order.appointmentId,
    esat6Result: esat6Result?.result,
    cfp10Result: cfp10Result?.result,
    interpretation: order.interpretation,
    file: order.fileDetails?.fileName
      ? {
        fileName: order.fileDetails.fileName,
        file: '',
      }
      : undefined,
    notes: order.notes,
    isTodo: false,
  };
}

function transformLabOrderToQuantiferonTestDto(order: LabOrderDto): CreateQuantiferonTestDto {
  const nilResult = order.results.find(
    (result) => result.type === LabOrderResultDtoType.QUANTIFERON_NIL_NOT_STIMULATED,
  );
  const mitogenResult = order.results.find((result) => result.type === LabOrderResultDtoType.QUANTIFERON_MITOGEN);
  const qft1Result = order.results.find((result) => result.type === LabOrderResultDtoType.QUANTIFERON_TB_WERT_1);
  const qft2Result = order.results.find((result) => result.type === LabOrderResultDtoType.QUANTIFERON_TB_WERT_2);
  const overallResult = order.results.find((result) => result.type === LabOrderResultDtoType.QUANTIFERON_FINDING);

  return {
    patientId: order.patient.id,
    samplingDate: order.samplingDate,
    samplingTime: order.samplingTime,
    samplingAddress: order.samplingAddress,
    samplingContact: order.samplingContact,
    appointmentId: order.appointmentId,
    nilResult: nilResult?.result,
    mitogenResult: mitogenResult?.result,
    qft1Result: qft1Result?.result,
    qft2Result: qft2Result?.result,
    overallResult: overallResult?.result,
    interpretation: order.interpretation,
    file: order.fileDetails?.fileName
      ? {
        fileName: order.fileDetails.fileName,
        file: '',
      }
      : undefined,
    notes: order.notes,
    isTodo: false,
  };
}

function getLabOrderDtoInterpretationNameFromString(type: string) {
  const interpretationResult = Object.values(LabOrderDtoInterpretation).filter(
    (interpretation) => type == interpretation,
  );
  if (interpretationResult && interpretationResult.length == 1) {
    const nameFromEnum = getLabOrderResultInterpretationName(interpretationResult[0]);
    if (nameFromEnum) {
      return nameFromEnum;
    }
  }

  return type;
}

function getLabOrderXrayFollowupNameFromString(type: string) {
  const interpretationResult = getLabOrderXrayFollowupStatusFromString(type);
  if (interpretationResult) {
    const nameFromEnum = getXrayFollowupName(interpretationResult);
    if (nameFromEnum) {
      return nameFromEnum;
    }
  }

  return type;
}

function getLabOrderXrayFollowupStatusFromString(type: string) {
  const interpretationResult = Object.values(CreateXrayTestDtoFollowup).filter(
    (interpretation) => type == interpretation,
  );
  if (interpretationResult && interpretationResult.length == 1) {
    return interpretationResult[0];
  }
  return undefined;
}

function getLabOrderXrayInterpretationNameFromString(type: string) {
  const interpretationResult = getLabOrderXrayInterpretationStatusFromString(type);
  if (interpretationResult) {
    const nameFromEnum = getXrayInterpretationName(interpretationResult);
    if (nameFromEnum) {
      return nameFromEnum;
    }
  }

  return type;
}

function getLabOrderXrayInterpretationStatusFromString(type: string) {
  const interpretationResult = Object.values(CreateXrayTestDtoFinding).filter(
    (interpretation) => type == interpretation,
  );
  if (interpretationResult && interpretationResult.length == 1) {
    return interpretationResult[0];
  }
  return undefined;
}

function getSputumTestMaterialDto(
  results: LabOrderResultDto[],
  testMaterial: LabOrderResultDtoType,
  testMaterialResultPrefix: string,
): SputumTestMaterialDto | undefined {
  const material = results.find((result) => result.type == testMaterial);

  if (material) {
    const materialResults = results.filter((result) => result.type.startsWith(testMaterialResultPrefix));
    return {
      otherMaterial: material.result ?? null,
      testMaterial: getSputumTestMaterialTypeFromLabOrderResultType(material.type)!,
      testMaterialResults: materialResults.map((result) => getSputumTestMaterialResultDto(result)),
    };
  }
}

function getSputumTestMaterialResultDto(result: LabOrderResultDto): SputumTestMaterialResultDto {
  return {
    detectionMethod: getSputumTestMaterialResultDetectionMethodFromLabOrderResultType(result.type)!,
    testResult: getSputumTestMaterialResultFromLabOrderResult(result.result)!,
  };
}

function getSputumTestMaterialTypeFromLabOrderResultType(resultType: LabOrderResultDtoType) {
  switch (resultType) {
    case LabOrderResultDtoType.SPUTUM_MATERIAL_SPUTUM:
      return SputumTestMaterialDtoTestMaterial.SPUTUM_FROM_THE_AIRWAYS;
    case LabOrderResultDtoType.SPUTUM_MATERIAL_AIRWAYS:
      return SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL_FROM_THE_AIRWAYS;
    case LabOrderResultDtoType.SPUTUM_MATERIAL_CUSTOM:
      return SputumTestMaterialDtoTestMaterial.OTHER_MATERIAL;
  }
}

function getSputumTestMaterialResultDetectionMethodFromLabOrderResultType(resultType: LabOrderResultDtoType) {
  switch (resultType) {
    case LabOrderResultDtoType.SPUTUM_METHOD_SPUTUM_CULTURE:
    case LabOrderResultDtoType.SPUTUM_METHOD_AIRWAYS_CULTURE:
    case LabOrderResultDtoType.SPUTUM_METHOD_CUSTOM_CULTURE:
      return SputumTestMaterialResultDtoDetectionMethod.CULTURE;
    case LabOrderResultDtoType.SPUTUM_METHOD_SPUTUM_MOLECULAR:
    case LabOrderResultDtoType.SPUTUM_METHOD_AIRWAYS_MOLECULAR:
    case LabOrderResultDtoType.SPUTUM_METHOD_CUSTOM_MOLECULAR:
      return SputumTestMaterialResultDtoDetectionMethod.MOLECULAR_BIOLOGICAL_DIRECT_DETECTION;
    case LabOrderResultDtoType.SPUTUM_METHOD_SPUTUM_MICROSCOPICAL:
    case LabOrderResultDtoType.SPUTUM_METHOD_AIRWAYS_MICROSCOPICAL:
    case LabOrderResultDtoType.SPUTUM_METHOD_CUSTOM_MICROSCOPICAL:
      return SputumTestMaterialResultDtoDetectionMethod.MICROSCOPIC_DIRECT_DETECTION;
  }
}

function getSputumTestMaterialResultFromLabOrderResult(result: string) {
  return Object.values(SputumTestMaterialResultDtoTestResult).find((testResult) => result == testResult);
}
